/**
 * Deep link `/@никнейм` и `/+<код-приглашения>` (SCREEN_CATALOG §15, BS §11.3): изменение v1.7 — пространство имён
 * у людей и каналов общее, поэтому ссылка разбирается одним запросом `GET /usernames/{username}`
 * и ведёт либо в профиль P-04, либо в карточку канала C-31. Неизвестный или недоступный никнейм —
 * нейтральное состояние без раскрытия причины.
 */
import { Navigate, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { api, unwrap } from '@shared/api/client'
import { normalizeUsername } from '@shared/lib/username'
import { Button, EmptyState, Spinner } from '@shared/ui'

import { Screen } from '../../layouts/Screen'

export function HandleScreen(): React.ReactElement {
  const { handle } = useParams({ strict: false }) as { handle?: string }
  const navigate = useNavigate()
  const raw = handle ?? ''
  const isHandle = raw.startsWith('@') && raw.length > 1
  /*
   * Ссылка-приглашение `/+<код>` (как `t.me/+…`) отдельным маршрутом не описывается: роутер не
   * умеет литерал перед параметром внутри сегмента. Поэтому она приходит сюда же и отсюда
   * уходит на экран приглашения — иначе её уносило в «Движ» как неизвестный путь.
   */
  const inviteCode = raw.startsWith('+') && raw.length > 1 ? raw.slice(1) : null
  const username = normalizeUsername(raw)

  const { data, isLoading, error } = useQuery({
    queryKey: ['resolve-username', username.toLowerCase()],
    queryFn: async () => unwrap(await api.GET('/usernames/{username}', { params: { path: { username } } })),
    enabled: isHandle,
    retry: false,
  })

  if (inviteCode) {
    return <Navigate to="/invite/$inviteCode" params={{ inviteCode }} replace />
  }

  // Не никнейм — обычный неизвестный путь: раздел по умолчанию без экрана ошибки.
  if (!isHandle) return <Navigate to="/dvizh" replace />

  if (data?.kind === 'channel' && data.channel) {
    return <Navigate to="/channels/$channelId" params={{ channelId: data.channel.id }} replace />
  }
  if (data?.user) return <Navigate to="/users/$userId" params={{ userId: data.user.id }} replace />

  if (isLoading) {
    return (
      <Screen title="Профиль" back>
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}>
          <Spinner size={28} />
        </div>
      </Screen>
    )
  }

  return (
    <Screen title="Профиль" back>
      <EmptyState
        icon="search"
        title={`@${username} не найден`}
        text={
          error
            ? 'Проверьте написание — возможно, пользователь сменил никнейм или закрыл профиль.'
            : 'Никнейм не найден.'
        }
        actions={
          <Button variant="primary" onClick={() => void navigate({ to: '/chats' })}>
            К чатам
          </Button>
        }
      />
    </Screen>
  )
}
